import { SellTicketModelType } from '@teinor/erp/types/company/order/sell/sellTicket'
import { useMemo } from 'react'
import ITHeaderText, { ITHeaderTextCustomer } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import InputTableLeftColumnForm from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { InputTableLeftColumnChange, InputTableLeftTabsProps } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { OrderHeaderURLs, SetCompleteDataTy } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { FormElement, FormRender } from '../../../../../../../../../forms/FormRenderer/types'
import { parseAddressToOneLine } from '../../../../../../../../../utils/globals/address'
import { displayDate } from '../../../../../../../../../utils/globals/date'
import { TwinDictionary } from '../../../../../../../../../utils/globals/dictionary'
import { returnClassOfSpanTemplate } from '../../../../../../../../../utils/globals/tailwind'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { getActiveCurrenciesDictionary } from '../../../../../../../../../utils/reducers/getters'


type SellTicketReadOnlyHeaderMainProps = InputTableLeftTabsProps & {
    orderHeaderURLs: OrderHeaderURLs
}

const SellTicketReadOnlyHeaderMain: React.FC<SellTicketReadOnlyHeaderMainProps> = ({ headerData, setCompleteData, handleChange }) => {
    const { t } = useTwinTranslation()
    const { memorizedAddresses, activeCurrencies} = useSellTicketReadOnlyHeaderMainLogic({ headerData, setCompleteData, handleChange })
    let extraFields: FormElement<keyof SellTicketModelType>[] = []
    let extraCustomerField: FormElement<keyof SellTicketModelType>[] = []
    let extraRefundDateFields: FormElement<keyof SellTicketModelType>[] = []
    if (headerData?.CustomerId) {
        extraCustomerField.push(
            {
                name: 'CustomerId',
                component: 'Custom',
                render: () => <ITHeaderTextCustomer label={t('customer', 'Cliente')} headerData={headerData} className={returnClassOfSpanTemplate(5)} />
            },
        )
    }
    if (headerData.CustomerAddressId) {
        extraFields.push({
            name: 'CustomerAddressId',
            component: 'Custom',
            render: ({ value }) => {
                return <ITHeaderText label={t('fiscalAddress', 'Dirección Fiscal')} value={memorizedAddresses.customerAddress} className={returnClassOfSpanTemplate(5)} />
            },
            extraProps: {
                customerAddress: memorizedAddresses.customerAddress
            }
        })
    }
    if (headerData.maxRefundDate) {
        extraRefundDateFields.push({
            name: 'maxRefundDate' as any,
            component: 'Custom',
            render: ({ value }) => <ITHeaderText label={t('maxRefundDate', 'Fecha máxima de devolución')} value={displayDate(new Date(value))} className={returnClassOfSpanTemplate(5)} />
        })
    }
    const sections: FormRender<SellTicketModelType> = [
        {
            cols: 5,
            elements: [
                {
                    name: 'CashRegisterId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('cashRegister', 'Caja')} value={headerData?.CashRegister?.full_assigned_number} className={returnClassOfSpanTemplate(5)} />
                },
                ...extraCustomerField,
                {
                    name: 'RepresentativeEmployeeId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('representative', 'Representante')} value={headerData?.RepresentativeEmployee?.fullname_short} className={returnClassOfSpanTemplate(5)} />
                },
                {
                    name: 'processed_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('date', 'Fecha')} value={displayDate(new Date(value))} className={returnClassOfSpanTemplate(3)} />
                },
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('assignedN', 'Nº asignado')} value={value} className={(headerData?.assigned_number ? '' : 'text-red-BA') + ' ' + returnClassOfSpanTemplate(2)} />,
                },
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('soldBy', 'Vendido por')} value={headerData?.WorkingCenter?.name || ''} className={returnClassOfSpanTemplate(3)} />
                },
                {
                    name: 'WorkingCenterStationId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('workingCenterStation', 'Puesto')} value={headerData?.WorkingCenterStation?.name || ''} className={returnClassOfSpanTemplate(3)} />
                },
                {
                    name: 'BillingWorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('billingBy', 'Facturado por')} value={headerData?.BillingWorkingCenter?.name || ''} className={returnClassOfSpanTemplate(3)} />
                    },
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie || ''} className={returnClassOfSpanTemplate(2)} />
                    },
                },
                ...extraFields,
                {
                    name: 'WorkingCenterAddressId',
                    component: 'Custom',
                    render: ({ value, extraProps }) => {
                        return <ITHeaderText label={t('workingCenterAddress', 'Dirección centro de trabajo')} value={memorizedAddresses?.workingCenterAddress} className={returnClassOfSpanTemplate(5)} />
                    },
                    extraProps: {
                        workingCenterAddress: memorizedAddresses?.workingCenterAddress
                    }
                },
                ...extraRefundDateFields,
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('currency', 'Moneda')} value={activeCurrencies?.[value]?.name} className={returnClassOfSpanTemplate(5)} />
                },
            ]
        }
    ]
    return (
        <InputTableLeftColumnForm form={sections} handleChange={handleChange} headerData={headerData} />
    )
}

interface SellTicketReadOnlyHeaderMainLogicProps {
    headerData: TwinDictionary
    setCompleteData: SetCompleteDataTy
    handleChange: InputTableLeftColumnChange
}

export const useSellTicketReadOnlyHeaderMainLogic = ({ headerData }: SellTicketReadOnlyHeaderMainLogicProps) => {
    const activeCurrencies = getActiveCurrenciesDictionary()
    const WorkingCenterAddress = headerData.WorkingCenterAddress
    const CustomerAddress = headerData.CustomerAddress
    const memorizedAddresses = useMemo(() => {
        return {
            workingCenterAddress: parseAddressToOneLine(WorkingCenterAddress),
            customerAddress: parseAddressToOneLine(CustomerAddress)
        }
    }, [WorkingCenterAddress, CustomerAddress])

    return { memorizedAddresses, activeCurrencies }

}

export default SellTicketReadOnlyHeaderMain